import {
  authService,
  AuthSessionExpiredError,
} from "../services/authService.tsx";

export interface FetchWithAuthOptions extends RequestInit {
  token?: string;
  ignoreStatus?: number[];
  headers?: Record<string, string>;
  authRetry?: boolean;
}

export async function fetchWithAuth<T = any>(
  url: string,
  options: FetchWithAuthOptions = {}
): Promise<T> {
  const { token, ignoreStatus = [], authRetry = false, ...restOptions } = options;

  // Context consumers can temporarily hold the previous token after a refresh.
  // localStorage is updated atomically by authService, so it is canonical here.
  let currentToken = authService.getAuthData().token ?? token;

  // Refresh token if needed
  if (authService.isTokenExpired()) {
    currentToken = await authService.refreshToken();
  }

  const headers: Record<string, string> = {
    ...(currentToken && { Authorization: `Bearer ${currentToken}` }),
    ...(restOptions.body && !options.headers?.["Content-Type"] && { "Content-Type": "application/json" }),
    ...(options.headers || {}),
  };

  try {
    const response = await fetch(url, {
      ...restOptions,
      headers,
      credentials: "include",
    });

    // The legacy backend returns 403 when jwt.verify rejects an expired/invalid
    // access token. Refresh once for either auth status, then fail normally so
    // genuine authorization failures cannot cause a retry loop.
    if ((response.status === 401 || response.status === 403) && !authRetry) {
      const newToken = await authService.handleUnauthorized();
      return fetchWithAuth<T>(url, { ...options, token: newToken, authRetry: true });
    }

    if (response.status === 401 && authRetry) {
      const error = new AuthSessionExpiredError();
      authService.announceExpiredSession();
      throw error;
    }

    // Special case: ignore specific status codes (e.g., 404)
    if (!response.ok) {
      if (ignoreStatus.includes(response.status)) {
        return response.json();
      }
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    return response.json();
  } catch (error) {
    console.error("Fetch error:", error);
    throw error;
  }
}

export const triggerModal = (): void => {
  authService.announceExpiredSession();
};
